import { CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined, DeleteOutlined, EditOutlined, ExpandOutlined, FileTextOutlined, LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, Modal, Typography } from 'antd';
import { Artifact, ArtifactTranscript } from 'components/Artifacts/types';
import { AppAudioPlayer } from 'components/UiKit/Media/AppAudioPlayer';
import { useState } from 'react';

const { Paragraph, Text, Title } = Typography;

type ArtifactDetailViewerProps = {
  artifact: Artifact | null;
  canMoveEarlier: boolean;
  canMoveLater: boolean;
  isDescriptionExpanded: boolean;
  isDeleting?: boolean;
  isReordering?: boolean;
  isTranscriptExpanded: boolean;
  onDelete: () => void;
  onToggleDescription: () => void;
  onEdit: () => void;
  onMoveEarlier: () => void;
  onMoveLater: () => void;
  onToggleTranscript: () => void;
};

const EXPANDABLE_TEXT_PREVIEW_LENGTH = 280;

const getTranscriptStatusTone = (transcript: ArtifactTranscript) => {
  switch (transcript.status) {
    case 'COMPLETED':
      return { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed' } as const;
    case 'FAILED':
      return { color: 'error', icon: <WarningOutlined />, label: 'Failed' } as const;
    case 'PROCESSING':
      return { color: 'processing', icon: <LoadingOutlined />, label: 'Processing' } as const;
    case 'QUEUED':
      return { color: 'warning', icon: <ClockCircleOutlined />, label: 'Queued' } as const;
    default:
      return { color: 'default', icon: <FileTextOutlined />, label: 'Not requested' } as const;
  }
};

const getMemoryLabel = (artifact: Artifact) => {
  const parts = [artifact.memoryDateLabel, artifact.memoryDate].filter(Boolean);
  return parts.length ? parts.join(' • ') : null;
};

export function ArtifactDetailViewer({
  artifact,
  canMoveEarlier,
  canMoveLater,
  isDescriptionExpanded,
  isDeleting = false,
  isReordering = false,
  isTranscriptExpanded,
  onDelete,
  onToggleDescription,
  onEdit,
  onMoveEarlier,
  onMoveLater,
  onToggleTranscript
}: ArtifactDetailViewerProps) {
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);

  if (!artifact) {
    return (
      <div className="artifacts-page__panel">
        <Empty description="Select an artifact to review its details." />
      </div>
    );
  }

  const transcriptTone = getTranscriptStatusTone(artifact.transcript);
  const memoryLabel = getMemoryLabel(artifact);
  const description = artifact.description?.trim() || '';
  const transcriptText = artifact.transcript.text || '';
  const hasLongDescription = description.length > EXPANDABLE_TEXT_PREVIEW_LENGTH;
  const hasTranscriptText = transcriptText.trim().length > 0;
  const hasLongTranscript = transcriptText.length > EXPANDABLE_TEXT_PREVIEW_LENGTH;
  const readyAudio = artifact.audio?.status === 'READY' && artifact.audio.url ? artifact.audio : null;
  const descriptionPreview = hasLongDescription && !isDescriptionExpanded
    ? `${description.slice(0, EXPANDABLE_TEXT_PREVIEW_LENGTH - 3)}...`
    : description;
  const transcriptPreview = hasLongTranscript && !isTranscriptExpanded
    ? `${transcriptText.slice(0, EXPANDABLE_TEXT_PREVIEW_LENGTH - 3)}...`
    : transcriptText;
  const imageUrl = artifact.image?.url || '';
  const hasImage = Boolean(imageUrl);
  const shouldShowAudioSection = Boolean(artifact.audio);
  const shouldShowTranscriptSection = hasTranscriptText || artifact.transcript.status !== 'NOT_REQUESTED';
  const shouldShowTranscriptChip = artifact.transcript.status !== 'NOT_REQUESTED' && artifact.transcript.status !== 'COMPLETED';

  return (
    <div className="artifact-viewer">
      <button
        type="button"
        className={`artifact-viewer__media${hasImage ? ' artifact-viewer__media--interactive' : ''}`}
        onClick={hasImage ? () => setIsImagePreviewOpen(true) : undefined}
        disabled={!hasImage}
        aria-label={hasImage ? `Expand image for ${artifact.title || 'selected artifact'}` : undefined}
      >
        {hasImage ? (
          <img className="artifact-viewer__image" src={imageUrl} alt={artifact.title || 'Selected artifact'} />
        ) : (
          <div className="artifact-viewer__image-fallback">
            <Empty description="Image unavailable" />
          </div>
        )}

        {hasImage ? (
          <div className="artifact-viewer__zoom-chip" aria-hidden="true">
            <ExpandOutlined />
          </div>
        ) : null}
      </button>

      <div className="artifact-viewer__content">
        <div>
          <p className="artifact-viewer__eyebrow">Selected artifact</p>
          <Title className="artifact-viewer__title" level={2}>
            {artifact.title || 'Untitled artifact'}
          </Title>
          {memoryLabel ? (
            <div className="artifact-viewer__date-row">
              <CalendarOutlined />
              <span>{memoryLabel}</span>
            </div>
          ) : null}
        </div>

        {description ? (
          <div className="artifact-viewer__section artifact-viewer__description-section">
            <Paragraph className="artifact-viewer__description">{descriptionPreview}</Paragraph>

            {hasLongDescription ? (
              <Button type="link" className="artifact-viewer__transcript-toggle" onClick={onToggleDescription} aria-expanded={isDescriptionExpanded}>
                {isDescriptionExpanded ? 'Hide description' : 'Show description'}
              </Button>
            ) : null}
          </div>
        ) : null}

        {shouldShowAudioSection ? (
          <div className="artifact-viewer__section">
            <p className="artifact-viewer__section-label">Audio story</p>
            <div className="artifact-viewer__audio-shell">
              {readyAudio ? (
                <AppAudioPlayer
                  url={readyAudio.url}
                  mimeType={readyAudio.mimeType || undefined}
                  fallback={<Text>Audio unavailable.</Text>}
                />
              ) : artifact.audio ? (
                <Paragraph className="artifact-viewer__muted">{artifact.audio.status === 'FAILED' ? 'Audio story is temporarily unavailable.' : 'Audio story is still processing.'}</Paragraph>
              ) : null}
            </div>
          </div>
        ) : null}

        {shouldShowTranscriptSection ? (
          <div className="artifact-viewer__section">
            <p className="artifact-viewer__section-label">Transcript excerpt</p>

            {!hasTranscriptText && artifact.transcript.status === 'FAILED' && (
              <Alert
                type="warning"
                showIcon
                message={artifact.transcript.errorCode || 'Transcription failed. You can edit the artifact and try again.'}
              />
            )}

            {!hasTranscriptText && artifact.transcript.status !== 'FAILED' && (
              <p className="artifact-viewer__transcript-card artifact-viewer__transcript-placeholder">
                Transcript updates will appear here when processing finishes.
              </p>
            )}

            {hasTranscriptText ? <p className="artifact-viewer__transcript-card">“{transcriptPreview}”</p> : null}

            {hasLongTranscript ? (
              <Button type="link" className="artifact-viewer__transcript-toggle" onClick={onToggleTranscript} aria-expanded={isTranscriptExpanded}>
                {isTranscriptExpanded ? 'Hide transcript' : 'Show transcript'}
              </Button>
            ) : null}
          </div>
        ) : null}
        {shouldShowTranscriptChip ? (
          <div className="artifact-viewer__chips">
            {shouldShowTranscriptChip ? (
              <span className="artifact-viewer__chip">
                {transcriptTone.icon} Transcript {transcriptTone.label}
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="artifact-viewer__actions">
          <Button type="primary" icon={<EditOutlined />} onClick={onEdit} disabled={artifact.canEdit === false}>
            Edit Artifact
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={onDelete} loading={isDeleting} disabled={artifact.canDelete === false}>
            Delete Artifact
          </Button>
        </div>

        <div className="artifact-viewer__secondary-actions">
          <Button onClick={onMoveEarlier} disabled={!canMoveEarlier || isReordering}>
            Move earlier
          </Button>
          <Button onClick={onMoveLater} disabled={!canMoveLater || isReordering}>
            Move later
          </Button>
        </div>

      </div>

      <Modal
        open={isImagePreviewOpen}
        onCancel={() => setIsImagePreviewOpen(false)}
        footer={null}
        centered
        width="min(92vw, 1080px)"
        title={null}
        closable={false}
        wrapClassName="artifact-viewer__preview-modal"
        styles={{
          body: { padding: 0 },
          content: {
            background: 'transparent',
            boxShadow: 'none',
            padding: 0
          }
        }}
      >
        {hasImage ? (
          <div className="artifact-viewer__preview-shell">
            <img className="artifact-viewer__preview-image" src={imageUrl} alt={artifact.title || 'Selected artifact'} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default ArtifactDetailViewer;